import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trash2, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Cart() {
  const navigate = useNavigate();
  const { cart, removeFromCart, getCartTotal, fetchCart } = useCart();
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProductDetails();
  }, [cart]);

  const fetchProductDetails = async () => {
    if (!cart.items || cart.items.length === 0) {
      setLoading(false);
      return;
    }

    try {
      const productMap = {};
      for (const item of cart.items) {
        const response = await axios.get(`${API}/products/${item.product_id}`);
        productMap[item.product_id] = response.data;
      }
      setProducts(productMap);
    } catch (error) {
      console.error('Failed to fetch product details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (productId) => {
    await removeFromCart(productId);
    toast.success('Removed from cart');
  };

  const handleCheckout = () => {
    navigate('/checkout');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-12" data-testid="empty-cart">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ShoppingBag className="h-24 w-24 mx-auto text-gray-300 mb-6" />
          <h2 className="text-3xl font-bold mb-4">Your cart is empty</h2>
          <p className="text-gray-600 mb-8">Start shopping and add items to your cart!</p>
          <Link to="/products">
            <Button className="bg-[#FF6B35] hover:bg-[#ff8c5a] text-white rounded-full px-8 py-6 text-lg">
              Browse Products
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="cart-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-8" data-testid="cart-title">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map((item) => {
              const product = products[item.product_id];
              if (!product) return null;

              return (
                <Card key={item.product_id} className="p-6 border-0 shadow-md" data-testid={`cart-item-${item.product_id}`}>
                  <div className="flex items-center gap-6">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <Link to={`/products/${product.id}`}>
                        <h3 className="text-lg font-semibold hover:text-[#FF6B35] transition-colors">
                          {product.name}
                        </h3>
                      </Link>
                      <p className="text-sm text-gray-600 mt-1">{product.category}</p>
                      <p className="text-lg font-bold text-[#FF6B35] mt-2">${item.price.toFixed(2)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-2">Quantity</p>
                      <p className="text-lg font-semibold" data-testid={`quantity-${item.product_id}`}>{item.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold mb-4" data-testid={`subtotal-${item.product_id}`}>
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => handleRemove(item.product_id)}
                        className="rounded-full"
                        data-testid={`remove-${item.product_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Order Summary */}
          <div>
            <Card className="p-6 border-0 shadow-md sticky top-24">
              <h2 className="text-2xl font-bold mb-6">Order Summary</h2>
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span data-testid="cart-subtotal">${getCartTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className="text-green-600 font-semibold">FREE</span>
                </div>
                <div className="border-t pt-4">
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span className="text-[#FF6B35]" data-testid="cart-total">${getCartTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>
              <Button
                onClick={handleCheckout}
                className="w-full bg-[#FF6B35] hover:bg-[#ff8c5a] text-white py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
                data-testid="checkout-button"
              >
                Proceed to Checkout
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
